import React, { useMemo } from 'react';
import styled from 'styled-components';
import classnames from 'classnames';
import Select, { components } from 'react-select';
import { Sprite } from 'components';

const Container = styled.div`
	height: 30px;
	position: relative;

	&.big {
		height: 40px;
	}

	.react-select__input {
		height: 100% !important;
	}

	&.small {
		height: 30px;
	}

	&.border {
		border: 1px solid ${props => props.theme.inputBorderColor};
		border-radius: 2px;
	}
`;

const styles = {
	container: provided => ({
		...provided,
		// height: border ? 'calc(100% - 2px)' : '100%',
		height: '100%',
	}),
	control: (provided, { isDisabled }) => {
		return {
			...provided,
			height: '100%',
			minHeight: '100%',
			border: 'none',
			boxShadow: 'none',
			borderRadius: '2px',
			backgroundColor: isDisabled ? '#D5E3F0' : 'white',
		};
	},
	valueContainer: provided => ({
		...provided,
		// height: border ? 'calc(100% - 2px)' : '100%',
		height: '100%',
		maxHeight: '100%',
		padding: '0 10px',
		input: {
			height: '100% !important',
			maxHeight: '100% !important',
		}
	}),
	placeholder: provided => ({
		...provided,
		margin: 0,
		display: 'block',
		color: '#4F7EA7',
		fontSize: '16px',
		lineHeight: '21px',
		fontFamily: "'PT Sans', sans-serif !important",
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	}),
	input: provided => {
		return {
			...provided,
			// height: border ? 'calc(100% - 2px)' : '100%',
			height: '100% !important',
			maxHeight: '100% !important',
			// overflowY: 'hidden',
			overflow: 'hidden',
			padding: 0,
			margin: 0,
			boxShadow: 'none',
			input: {
				boxShadow: 'none !important',
				// maxHeight: border ? 'calc(100% - 2px)' : '100%',
				maxHeight: '100% !important',
				height: '100% !important',
				// height: 'auto !important',
				display: 'block',
				color: '#004685 !important',
				fontSize: '16px !important',
				lineHeight: '21px !important',
				fontFamily: "'PT Sans', sans-serif !important",
				transition: 'none !important',

				width: '100%',
				'&:focus': {
					gridArea: '1 / 2 / auto / span 3 !important',
					background: 'white !important',
				}
			}
			// border: '1px solid blue',
		};
	},
	singleValue: (provided, state) => {
		const color = state?.data?.color ?? '#004685 !important';

		return {
			...provided,
			// gridArea: '1/1/1/2',
			gridArea: '1/1/1/3',
			color,
			padding: 0,
			margin: 0,
			fontSize: '16px !important',
			lineHeight: '21px !important',
			fontFamily: "'PT Sans', sans-serif !important",
			'> div': {
				fontSize: '16px !important',
				lineHeight: '21px !important',
				fontFamily: "'PT Sans', sans-serif !important",
				color,
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'center',
				'svg': {
					marginRight: '10px',
				}
			}
		};
	},
	indicatorsContainer: provided => ({
		...provided,
		height: '100%',
		maxHeight: '100%'
	}),
	indicatorSeparator: provided => ({
		...provided,
		display: 'none',
	}),
	dropdownIndicator: provided => ({
		...provided,
		color: '#004685 !important',
	}),
	menu: provided => ({
		...provided,
		marginTop: 0,
		marginBottom: 0,
		marginLeft: '-1px',
		width: 'calc(100% + 2px)',
		// borderRadius: 0,
		zIndex: 999999,
		padding: 0,
		border: '1px solid #004685',
		borderRadius: '2px',
	}),
	menuPortal: provided => ({
		...provided,
		zIndex: 9999,
	}),
	menuList: provided => ({
		...provided,
		margin: 0,
		padding: 0,
		maxHeight: '230px'
	}),
	option: (provided, state) => {
		let color = state?.data?.color ?? '#004685';
		let backgroundColor = 'transparent';

		if (state?.isSelected) {
			color = 'white';
			backgroundColor = state?.data?.color ?? provided.backgroundColor;
		} else if (state?.isFocused) {
			backgroundColor = '#D5E3F0';
		}

		return {
			...provided,
			minHeight: state?.selectProps?.big ? '40px' : '30px',
			paddingTop: '5px',
			paddingBottom: '5px',
			paddingLeft: `${10 + ((state?.data?.deep ?? 0) * 20)}px`,
			paddingRight: '10px',
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			'&:not(:first-of-type)': {
				borderTop: '1px solid #D5E3F0',
			},
			color,
			backgroundColor,
			'> div': {
				fontSize: '16px !important',
				lineHeight: '21px !important',
				fontFamily: "'PT Sans', sans-serif !important",
				color,
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'center',
				'svg': {
					marginRight: '10px',
				}
			}
		};
	},
};

const DropdownIndicator = props => 
	<components.DropdownIndicator {...props}>
		<Sprite icon='arrow_down' />
	</components.DropdownIndicator>
;

export const SelectInput = ({
	small = false,
	border = true,
	name = '',
	big = false,
	placeholder = '',
	noOptionsMessage = () => null,
	filterOption = (candidate, input) => String(candidate?.label ?? '').toLowerCase().includes(input.toLowerCase()),
	classNames = '',
	menuPlacement = 'auto',
	...props
}) => {
	const getOptionValue = props?.getOptionValue !== undefined ? props.getOptionValue : (option => option?.Code ?? option?.value ?? '');
	const getOptionLabel = props?.getOptionLabel !== undefined ? props.getOptionLabel : (option => option?.Name ?? option?.label ?? '');

	const value = useMemo(() => {
		if (props?.value === undefined) return null;
		if (Array.isArray(props.options) && (typeof props.value === 'string' || typeof props.value === 'number')) return props.options.find(i => Number(getOptionValue(i)) === Number(props.value)) || null;
		return props.value;
	}, [ props.value, props.options ]);

	const bind = {
		...props,
		placeholder,
		noOptionsMessage,
		filterOption,
		styles,
		name,
		getOptionValue,
		getOptionLabel,
		components: {
			DropdownIndicator,
		},
		menuPlacement,
		value,
		big,
		menuPortalTarget: document.body,
		// menuShouldScrollIntoView: true,
		// defaultMenuIsOpen: true,
		// defaultMenuIsOpen: name === 'ContactPerson'
	};

	return (
		<Container className={classnames(classNames, 'select_container', `select_container__${name}`, { small, border, big })}>
			<Select {...bind} />
		</Container>
	);
};